/**
 * DetailViewCommon Component
 *
 * This component is used to display the common elements of an event detail screen across different platforms.
 * It handles data display such as the event's name, date, time, and location.
 *
 * Props:
 *  - route: Navigation route that includes event details.
 *  - FontAwesomeIcon: The FontAwesome icon component used for displaying icons.
 *  - iconSize: The size of the FontAwesome icon.
 *
 * @file DetailViewCommon.js
 * @module DetailViewCommon
 */
import React, { useEffect } from "react";
import { View, StyleSheet, Dimensions, Platform, Linking, TouchableOpacity, Pressable } from "react-native";
import { useNavigation } from "@react-navigation/native";
import { Text } from "react-native-paper";
import { COLORS, STRINGS } from "../../../constants";
import Heading from "../../../components/Heading/Heading";
import SummaryCard from "../../../components/SummaryCard/SummaryCard";
import {
  faGlobe,
  faCalendarAlt,
  faLocationDot,
  faPhone,
  faImage,
  faShareAlt,
  faLink,
  faGauge,
  faBuilding,
  faParking,
} from "@fortawesome/free-solid-svg-icons";
import DetailDescription from "../../../components/DetailDescription/DetailDescription";
import DetailList from "../../../components/DetailList/DetailList";
import DetailButtonArray from "../../../components/DetailButtonArray/DetailButtonArray";
import { formatAddress, toTitleCase, parkingFormatAddress } from "../../../utils/formatting";
import EventStore from "../../../storage/eventStore";
import ParkingStore from "../../../storage/parkingStore";
import { getNearbyLocations } from "../../../utils/geolocation";
import Loading from "../../../components/Loading/Loading";
import PageNotFound from "../../../components/NoResults/PageNotFound";
import { share, supportsShare, openDirections, addToCalendar } from "../../../utils/helper";
import { logAnalyticsEvent } from "../../../utils/firebase";
import useMetaDescriptionOnFocus from "../../../components/Meta/MetaDescription";

const DetailViewCommon = ({ route, FontAwesomeIcon, iconSize }) => {
  logAnalyticsEvent("screen_view", { name: "VBGo-EventsDetailView" });

  const featuredEvents = EventStore((state) => state.featuredEvents);
  const fetchEventByKey = EventStore((state) => state.fetchEventByKey);
  const selectedEvent = EventStore((state) => state.selectedEvent);
  const eventKey = route.params?.eventKey;
  const featuredEvent = featuredEvents.find((item) => item.occurrence_id === eventKey);
  const event = featuredEvent || (selectedEvent?.occurrence_id === eventKey ? selectedEvent : null);
  const initialLoading = EventStore((state) => state.initialLoading);
  const navigation = useNavigation();
  const allParkingData = ParkingStore((state) => state.parking);
  useMetaDescriptionOnFocus(event?.searchDescription);

  /**
   * Generates an array of header button configurations based on the properties of the current event.
   * Each button is conditionally included depending on the presence of relevant event data.
   *
   * @returns {Array<Object>} Array of button objects for the event header.
   * Each object may contain:
   *   - icon: Icon definition for the button.
   *   - label: Text label for the button.
   *   - color: Color value for the button.
   */
  const initHeaderButtons = () => {
    return [
      event?.images && event?.images?.length > 0 ? { icon: faImage, label: "Photos", color: COLORS.border } : null,
      event?.name && event?.location?.name && supportsShare()
        ? { icon: faShareAlt, label: "Share", color: COLORS.border }
        : null,
      event?.location?.url ? { icon: faGlobe, label: "Website", color: COLORS.border } : null,
      event?.occurrences?.[0].url ? { icon: faLink, label: "Link", color: COLORS.border } : null,
      event?.location?.phone && !(Platform.isPad || Platform.OS === "web")
        ? { icon: faPhone, label: "Call", color: COLORS.border }
        : null,
    ].filter(Boolean);
  };

  /**
   * handlePress
   *
   * This function is a simple handler for button press events. It logs the label of the pressed
   * button to the console for debugging or action tracking purposes.
   *
   * Props:
   * - button (object): The button object containing the `label` property to be logged.
   *
   * Returns:
   * - None (This function performs a console log as an action).
   */
  const handlePress = (button) => {
    const { label } = button;
    switch (label) {
      case "Share": {
        const url = `\n\n${process.env.REACT_APP_APPURL}/events/details?eventKey=${eventKey}`;
        const message = `Check out this event: ${event.name} at ${event.location.name}. Address: ${formattedAddress}${url}`;
        share(message, event.name);
        break;
      }
      case "Call":
        if (event.location?.phone) Linking.openURL(`tel:${event.location.phone}`);
        break;
      case "Website":
        if (event.location?.url) {
          let url = event.location.url;
          if (!url.startsWith("http://") && !url.startsWith("https://")) {
            url = "https://" + url;
          }
          Linking.openURL(url);
        }
        break;
      case "Link":
        if (event.occurrences[0].url) Linking.openURL(event.occurrences[0].url);
        break;
      case "Photos":
        navigation.navigate("ImageGalleryModal", {
          data: event,
          accessibilityLabel: `${event.name} ${STRINGS.picture}`,
        });
        break;
      default:
        console.log(`Button pressed: ${label}`);
    }
  };

  const parkingTypeIcons = {
    meter: faGauge,
    structure: faBuilding,
    default: faParking,
  };
  useEffect(() => {
    if (!event || Object.keys(event).length === 0) {
      fetchEventByKey(eventKey);
    }
  }, [event, eventKey]);

  useEffect(() => {
    if (event) {
      navigation.setOptions({
        title: event?.name,
        headerRight: () => (
          <DetailButtonArray
            FontAwesomeIcon={FontAwesomeIcon}
            buttons={initHeaderButtons()}
            onPress={handlePress}
            iconSize={Platform.OS === "web" ? "2x" : iconSize}
          />
        ),
        headerRightContainerStyle: styles.headerRight,
        headerTitleStyle: styles.headerTitle,
      });
    }
  }, [event]);

  if (initialLoading) {
    return (
      <View style={styles.container}>
        <Loading />
      </View>
    );
  }

   if (!event || Object.keys(event).length === 0) {
    return (
      <View style={styles.container}>
        <PageNotFound />
      </View>
    );
  }

  const occurrences = event?.occurrences?.[0];

  let parkingData = [];

  if (event.location && event.location.latitude && event.location.longitude) {
    parkingData = getNearbyLocations(
      {
        latitude: event.location.latitude,
        longitude: event.location.longitude,
      },
      allParkingData,
      0.2,
      5
    );
  }

  const formatted = occurrences?.formatted || {};
  const formattedDate = `${formatted?.startDate || ""}`;
  const formattedDay = new Date(occurrences?.start).toLocaleDateString("en-US", {
    weekday: "long",
  });
  const formattedTime = `${formatted?.times}`;

  const formattedAddress = formatAddress(event?.location?.address, event?.location?.city, event?.location?.zip);

  /**
   * renderItemName
   *
   * This function is responsible for rendering the name of a parking item with proper title casing.
   * It takes a name string, converts it to title case, and displays it using the parkingName style.
   *
   * Props:
   * - name (string): The name of the parking item to be displayed.
   *
   * Returns:
   * - A Text component displaying the formatted name.
   */
  const renderItemName = (name) => (
    <TouchableOpacity onPress={() => navigateToParkingDetails(name)}>
      <Text style={styles.parkingName}>{toTitleCase(name)}</Text>
    </TouchableOpacity>
  );

  /**
   * renderItemDetails
   *
   * This function renders the details of a parking item, including the number of available spaces
   * and the formatted address. It concatenates the available spaces with a dot separator and the
   * formatted address of the parking item.
   *
   * Props:
   * - item (object): The parking item containing `freeSpaces` and `address` properties.
   *
   * Returns:
   * - A Text component displaying the parking details in a specific format.
   */
  const renderItemDetails = (item) => (
    <TouchableOpacity onPress={() => navigateToParkingDetails(item.name)}>
      <Text style={styles.parkingDetails}>
        {/* {item.freeSpaces} {STRINGS.spacesAvailable}
        <Text style={styles.dotStyle}> • </Text> */}
        {parkingFormatAddress(item.address)}
      </Text>
    </TouchableOpacity>
  );

  const navigateToParkingDetails = (name) => {
    const index = allParkingData.findIndex((item) => item.name === name);
    if (index !== -1) {
      navigation.navigate("ParkingModal", { index });
    }
  };

  const headerComponent = () => (
    <>
      <Heading text={event.name} fontSize={26} />
      {event.categoriesV2 && event.categoriesV2.length > 0 && (
        <View style={styles.categoriesContainer}>
          <Text style={styles.categoriesHeader}>
            {event.categoriesV2.length === 1 ? STRINGS.category : STRINGS.categories}:
          </Text>
          <Text style={styles.categoriesText}>{event.categoriesV2.join(", ")}</Text>
        </View>
      )}
      <View style={styles.cardsContainer}>
        {occurrences ? (
          <Pressable
            testID="add-to-calendar"
            onPress={() =>
              addToCalendar(
                event.name,
                event.occurrences[0].start,
                event.occurrences[0].end,
                event.location?.address,
                event.description,
                event.occurrences[0].url
              )
            }
          >
            <SummaryCard
              heading={formattedDate}
              description={`${formattedDay}, ${formattedTime}`}
              icon={faCalendarAlt}
              size={iconSize}
              iconColor={COLORS.primary}
              FontAwesomeIcon={FontAwesomeIcon}
            />
          </Pressable>
        ) : (
          <SummaryCard
            heading={STRINGS.timingUnavailable}
            description={STRINGS.timingUnavailable}
            icon={faCalendarAlt}
            size={iconSize}
            iconColor={COLORS.warning}
            FontAwesomeIcon={FontAwesomeIcon}
          />
        )}
        <Pressable
          onPress={() => {
            if (event.location?.latitude && event.location?.longitude) {
              openDirections(event.location.latitude, event.location.longitude);
            }
          }}
        >
          <SummaryCard
            heading={event.location?.name || STRINGS.locationUnavailable}
            description={formattedAddress}
            icon={faLocationDot}
            size={iconSize}
            iconColor={COLORS.primary}
            FontAwesomeIcon={FontAwesomeIcon}
          />
        </Pressable>
      </View>
      <DetailDescription
        title={STRINGS.aboutEvent}
        description={event.description}
        FontAwesomeIcon={FontAwesomeIcon}
        iconSize={iconSize}
        disclaimer={!(event.location && (event.location.name || event.location.address))}
      />
      {parkingData.length > 0 && <Heading fontSize={18} text={STRINGS.parkingOptions} color={COLORS.textDark} />}
    </>
  );

  return (
    <View style={styles.container}>
      {initialLoading && <Loading />}
      {!initialLoading && (
        <DetailList
          data={parkingData}
          FontAwesomeIcon={FontAwesomeIcon}
          iconSize={Platform.OS === "web" ? "xl" : iconSize}
          icon={(item) => parkingTypeIcons[item.type] || parkingTypeIcons.default}
          renderItemName={renderItemName}
          renderItemDetails={renderItemDetails}
          headerComponent={headerComponent}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollContainer: {
    flexGrow: 1,
    justifyContent: "flex-start",
    paddingBottom: Dimensions.get("window").height * 0.1,
  },
  cardsContainer: {
    paddingTop: 16,
    paddingBottom: 16,
  },
  parkingName: {
    fontSize: 16,
    fontWeight: Platform.OS == "web" ? 500 : "bold",
    color: COLORS.textDark,
    lineHeight: 24,
    marginBottom: 4,
  },
  parkingDetails: {
    fontSize: 16,
    color: COLORS.border,
    lineHeight: 28,
  },
  dotStyle: {
    fontSize: 18,
    color: COLORS.border,
  },
  chipScrollContainer: {
    paddingVertical: 12,
    gap: 8,
    flexDirection: "row",
  },
  categoryChip: {
    marginRight: 8,
  },
  headerRight: {
    flexDirection: "row",
    alignItems: "center",
    paddingRight: 8,
    justifyContent: "flex-end",
  },
  headerTitle: {
    display: "none",
  },
  categoriesContainer: {
    marginTop: 21,
    gap: 8,
  },
  categoriesHeader: {
    fontSize: 18,
    fontWeight: "bold",
    color: COLORS.textDark,
  },
  categoriesText: {
    fontSize: 16,
    color: COLORS.border,
  },
});

export default DetailViewCommon;
